// ============================================================================
// UNIVERSAL ACTION HANDLERS - ENRICHMENT ACTIONS
// ============================================================================

import type { UniversalActionHandlers } from "./types";

/**
 * Enrichment Actions
 */
export const enrichmentActions: UniversalActionHandlers = {
  // Enrich contact action - calls API endpoint to enrich contact data
  enrichContact: async (item, context) => {
    try {
      context.onToast("Triggering contact enrichment...", "info");

      const contactId = item.contact_id || item.id;
      if (!contactId) {
        context.onToast("No contact ID available for enrichment", "warning");
        return;
      }

      console.log("📧 Enriching contact:", contactId);

      const response = await fetch("/api/enrichment/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ contactId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API returned status ${response.status}`);
      }

      console.log("✅ Contact enrichment triggered successfully");

      context.onToast("Contact enrichment triggered successfully", "success");

      // Refresh the item after a short delay to allow webhook to process
      setTimeout(() => {
        void context.onRefresh();
      }, 1000);
    } catch (error) {
      console.error("❌ Error triggering contact enrichment:", error);
      context.onToast(
        error instanceof Error ? error.message : "Failed to trigger contact enrichment",
        "error"
      );
    }
  },

  // Enrich company action - calls API endpoint to enrich company data
  enrichCompany: async (item, context) => {
    try {
      context.onToast("Triggering company enrichment...", "info");

      const companyId =
        context?.entityIds?.companyId ??
        item?.company_id ??
        item?.company?.id ??
        (context?.pageConfig?.entityName === "companies" ? item?.id : undefined);
      if (!companyId) {
        context.onToast("No company ID available for enrichment", "warning");
        return;
      }

      console.log("🏢 Enriching company:", companyId);

      const response = await fetch("/api/enrichment/company", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ companyId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API returned status ${response.status}`);
      }

      console.log("✅ Company enrichment triggered successfully");

      context.onToast("Company enrichment triggered successfully", "success");

      // Refresh the item after a short delay to allow webhook to process
      setTimeout(() => {
        void context.onRefresh();
      }, 1000);
    } catch (error) {
      console.error("❌ Error triggering company enrichment:", error);
      context.onToast(
        error instanceof Error ? error.message : "Failed to trigger company enrichment",
        "error"
      );
    }
  },

  // Re-score lead action - re-runs lead scoring for a company + applies fit/score tags
  rescoreLead: async (item, context) => {
    try {
      context.onToast("Re-scoring lead...", "info");

      const companyId =
        context?.entityIds?.companyId ??
        item?.company_id ??
        item?.company?.id ??
        (context?.pageConfig?.entityName === "companies" ? item?.id : undefined);
      if (!companyId) {
        context.onToast("No company ID available for scoring", "warning");
        return;
      }

      const response = await fetch("/api/lead-scoring/score-company", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ companyId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API returned status ${response.status}`);
      }

      context.onToast("Lead re-scored — fit tags updated", "success");

      // Refresh so the new tags show on the row/detail.
      setTimeout(() => {
        void context.onRefresh();
      }, 500);
    } catch (error) {
      console.error("❌ Error re-scoring lead:", error);
      context.onToast(
        error instanceof Error ? error.message : "Failed to re-score lead",
        "error"
      );
    }
  },
};
